import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Formik, Form, FormikHelpers } from "formik";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { TabView, TabPanel } from "primereact/tabview";
import {
  MediaUploader,
  SimpleTextField,
  LocationPhotoGallery,
  HoursSchedule,
} from "@components";
import { allowedFields, colClassMap, fields } from "./constants";
import { STRING } from "@constants";
import {
  LocationRead,
  LocationsUpdate,
  LocationPhoto,
  WeekSchedule,
} from "@types";
import { UseManageLocationFormProps } from "./types";
import { locationValidationSchema, buildHoursPayload } from "./utils";
import {
  useCreateLocation,
  useUpdateLocation,
  useUploadLocationImage,
  useUpdateLocationHoursOnly,
  convertGateScheduleToAPIFormat,
} from "@api";
import { useManageForm } from "@hooks";

export function LocationForm({
  editValues,
  onClose,
  onLocationUpdated,
}: UseManageLocationFormProps) {
  const { triggerCreateLocation } = useCreateLocation();
  const { triggerUpdateLocation } = useUpdateLocation();
  const { triggerUploadImage } = useUploadLocationImage();
  const { triggerUpdateHours, loading: isUpdatingHours } = useUpdateLocationHoursOnly();
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [locationPhotos, setLocationPhotos] = useState<LocationPhoto[]>(
    editValues?.photos || [],
  );
  const [currentHomePhotoUrl, setCurrentHomePhotoUrl] = useState<
    string | undefined
  >(editValues?.homePhotoUrl);
  const [officeSchedule, setOfficeSchedule] = useState<WeekSchedule | undefined>(
    editValues?.weekSchedule,
  );
  const [gateSchedule, setGateSchedule] = useState<WeekSchedule | undefined>(
    editValues?.gateAccessHours,
  );
  const [officeTimeoutId, setOfficeTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [gateTimeoutId, setGateTimeoutId] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setLocationPhotos(editValues?.photos || []);
    setCurrentHomePhotoUrl(editValues?.homePhotoUrl);
    setOfficeSchedule(editValues?.weekSchedule);
    setGateSchedule(editValues?.gateAccessHours);
  }, [editValues]);

  useEffect(() => {
    return () => {
      if (officeTimeoutId) clearTimeout(officeTimeoutId);
      if (gateTimeoutId) clearTimeout(gateTimeoutId);
    };
  }, [officeTimeoutId, gateTimeoutId]);


  const { sendForm } = useManageForm<LocationsUpdate, LocationRead>({
    createFn: (data) => triggerCreateLocation(data),
    updateFn: (id, data) => triggerUpdateLocation({ id, payload: data }),
    successMessage: {
      create: "Location successfully created",
      update: "Location Description successfully updated",
    },
  });

  const initialValues = useMemo(
    () => {
      const gateAccessRaw = editValues?.gateAccessHours as any;
      const gateHoursSaturdayDerived = (() => {
        const day = gateAccessRaw?.saturday;
        if (!day) return "";
        if (day.isClosed || (!day.open && !day.close)) return "Closed";
        return `${day.open} - ${day.close}`;
      })();
      const gateHoursSundayDerived = (() => {
        const day = gateAccessRaw?.sunday;
        if (!day) return "";
        if (day.isClosed || (!day.open && !day.close)) return "Closed";
        return `${day.open} - ${day.close}`;
      })();

      return {
      id: editValues?.id ?? "",
      locationName: editValues?.locationName ?? "",
      address: editValues?.address ?? "",
      address2: editValues?.address2 ?? "",
      city: editValues?.city ?? "",
      state: editValues?.state ?? "",
      zipCode: editValues?.zipCode ?? "",
      email: editValues?.email ?? "",
      phone: editValues?.phone ?? "",
      hoursWeekdays: editValues?.hoursWeekdays ?? "",
      hoursSaturday: editValues?.hoursSaturday ?? "",
      hoursSunday: editValues?.hoursSunday ?? "",
      gateHours: editValues?.gateHours ?? "",
      gateHoursSaturday: editValues?.gateHoursSaturday || gateHoursSaturdayDerived,
      gateHoursSunday: editValues?.gateHoursSunday || gateHoursSundayDerived,
      weekSchedule: editValues?.weekSchedule,
      gateAccessHours: editValues?.gateAccessHours,
      ownerName: editValues?.ownerName ?? "",
      };
    },
    [editValues],
  );

  const handleCancel = (dirty: boolean, resetForm: () => void) => {
    if (!dirty) return onClose();
    confirmDialog({
      message: STRING.LOCATION_FORM.CONFIRM_DIALOG_MESSAGE,
      header: STRING.LOCATION_FORM.CONFIRM_DIALOG_HEADER,
      icon: STRING.LOCATION_FORM.CONFIRM_DIALOG_ICON,
      acceptClassName: STRING.LOCATION_FORM.CONFIRM_DIALOG_ACCEPT_CLASS,
      accept: () => {
        resetForm();
        onClose();
      },
    });
  };

  const isScheduleComplete = (schedule: WeekSchedule) =>
    Object.values(schedule).every((day) => {
      if (!day.isOpen) return true;
      if (day.is24Hours) return true;
      return day.openTime && day.closeTime;
    });

  const handleOfficeScheduleChange = useCallback(
    (newSchedule: WeekSchedule) => {
      setOfficeSchedule(newSchedule);
      if (!isScheduleComplete(newSchedule)) return;
      if (officeTimeoutId) clearTimeout(officeTimeoutId);
      const id = setTimeout(() => {
        if (editValues?.id) triggerUpdateHours({ id: editValues.id, weekSchedule: newSchedule });
      }, 5000);
      setOfficeTimeoutId(id);
    },
    [editValues?.id, officeTimeoutId, triggerUpdateHours],
  );

  const handleGateScheduleChange = useCallback(
    (newSchedule: WeekSchedule) => {
      setGateSchedule(newSchedule);
      if (!isScheduleComplete(newSchedule)) return;
      if (gateTimeoutId) clearTimeout(gateTimeoutId);
      const id = setTimeout(() => {
        if (editValues?.id)
          triggerUpdateHours({ id: editValues.id, directHours: convertGateScheduleToAPIFormat(newSchedule) });
      }, 5000);
      setGateTimeoutId(id);
    },
    [editValues?.id, gateTimeoutId, triggerUpdateHours],
  );

  const handleSubmit = async (
    values: LocationsUpdate,
    formikHelpers: FormikHelpers<LocationsUpdate>,
  ) => {
    const { homePhotoUrl, weekSchedule: _, gateAccessHours: __, ...rest } = values;

    const savedLocation = await sendForm(rest);

    if (savedLocation) {
      // Convert text hour fields to JSON and save via /hours endpoint
      const hoursPayload = buildHoursPayload(
        rest.hoursWeekdays,
        rest.hoursSaturday,
        rest.hoursSunday,
        rest.gateHours,
        rest.gateHoursSaturday,
        rest.gateHoursSunday,
      );
      await triggerUpdateHours({
        id: savedLocation.id,
        directHours: hoursPayload,
      }).catch(() => {});

      if (onLocationUpdated) onLocationUpdated(savedLocation);
    }

    if (selectedImageFile && savedLocation?.id) {
      await triggerUploadImage({ id: savedLocation.id, file: selectedImageFile });
    }

    formikHelpers.resetForm();
    onClose();
  };

  return (
    <>
      <ConfirmDialog />
      <Formik
        initialValues={initialValues}
        validationSchema={locationValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnBlur
        validateOnChange
      >
        {({ isSubmitting, dirty, resetForm }) => (
          <Form className="flex flex-column h-full">
            {editValues?.id && (
              <TabView className="mb-3">
                <TabPanel
                  header="Location Information"
                  leftIcon="pi pi-map-marker mr-2"
                >
                  <div className="flex-1 overflow-auto py-4 px-3">
                    {/* Main Photo Section */}
                    <div className="mb-4">
                      <MediaUploader
                        key={currentHomePhotoUrl || "no-image"}
                        label="Main Location Photo"
                        initialUrl={currentHomePhotoUrl}
                        noFileText="No main photo selected"
                        selectedText="Photo selected"
                        onFileSelect={(file) => {
                          setSelectedImageFile(file);
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (e) => {
                              setCurrentHomePhotoUrl(
                                e.target?.result as string,
                              );
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                      <p className="text-sm text-500 mt-2 text-center">
                        <i className="pi pi-info-circle mr-1"></i>
                        This is the main photo displayed in listings and search
                        results
                      </p>
                    </div>

                    {/* Form Fields Section */}
                    <div className="mb-4">
                      <h2 className="text-xl font-bold text-surface-700">
                        {STRING.LOCATION_FORM.GENERAL_INFORMATION}
                      </h2>
                      <p className="text-sm text-surface-500">
                        {STRING.LOCATION_FORM.GENERAL_SUBTEXT}
                      </p>
                    </div>

                    <div className="grid formgrid p-fluid m-0 row-gap-4 grid-nogutter">
                      {fields
                        .filter(({ name }) => allowedFields.includes(name))
                        .map((field) => (
                          <div
                            key={field.name}
                            className={colClassMap[field.name] || "col-12 px-1"}
                          >
                            <p className="text-sm text-surface-500 mb-1 font-medium">
                              {field.label}
                            </p>
                            <SimpleTextField
                              id={field.id}
                              name={field.name}
                              label={field.label}
                              type={field.type}
                            />
                          </div>
                        ))}
                    </div>
                  </div>
                </TabPanel>

                <TabPanel header="Photo Gallery" leftIcon="pi pi-images mr-2">
                  <LocationPhotoGallery
                    locationId={editValues.id}
                    photos={locationPhotos}
                    currentMainPhotoUrl={currentHomePhotoUrl}
                    onPhotosUpdate={setLocationPhotos}
                    onMainPhotoChange={(newMainPhotoUrl) => {
                      if (newMainPhotoUrl) {
                        setCurrentHomePhotoUrl(newMainPhotoUrl);
                        setSelectedImageFile(null);
                      }
                    }}
                    maxPhotos={10}
                  />
                </TabPanel>

                <TabPanel header="Business Hours" leftIcon="pi pi-clock mr-2">
                  <div className="p-2 flex flex-column gap-4">
                    {/* Office Hours */}
                    <div>
                      <h3 className="text-base font-semibold text-surface-700 mb-2">
                        Office Hours
                      </h3>
                      <HoursSchedule
                        value={officeSchedule}
                        onChange={handleOfficeScheduleChange}
                        label="Office Hours"
                      />
                    </div>

                    {/* Gate Access Hours */}
                    <div>
                      <h3 className="text-base font-semibold text-surface-700 mb-2">
                        Gate Access Hours
                      </h3>
                      <HoursSchedule
                        value={gateSchedule}
                        onChange={handleGateScheduleChange}
                        label="Gate Access Hours"
                      />
                    </div>

                    <div className="surface-100 p-3 border-round">
                      <div className="flex align-items-center gap-2">
                        {isUpdatingHours ? (
                          <>
                            <i className="pi pi-spin pi-spinner text-primary" />
                            <span className="text-sm text-primary font-semibold">Saving...</span>
                          </>
                        ) : (
                          <p className="text-sm text-600 m-0">
                            <i className="pi pi-info-circle mr-1" />
                            Changes auto-save 5 seconds after you finish editing. You can also save manually.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </TabPanel>
              </TabView>
            )}

            {/* For new locations without tabs */}
            {!editValues?.id && (
              <div className="flex-1 overflow-auto py-4 px-3 bg-surface-50">
                <div className="mb-4">
                  <h2 className="text-xl font-bold text-surface-700">
                    {STRING.LOCATION_FORM.GENERAL_INFORMATION}
                  </h2>
                  <p className="text-sm text-surface-500">
                    {STRING.LOCATION_FORM.GENERAL_SUBTEXT}
                  </p>
                </div>

                <div className="grid formgrid p-fluid m-0 row-gap-4 grid-nogutter">
                  {fields
                    .filter(({ name }) => allowedFields.includes(name))
                    .map((field) => (
                      <div
                        key={field.name}
                        className={colClassMap[field.name] || "col-12 px-1"}
                      >
                        <p className="text-sm text-surface-500 mb-1 font-medium">
                          {field.label}
                        </p>
                        <SimpleTextField
                          id={field.id}
                          name={field.name}
                          label={field.label}
                          type={field.type}
                        />
                      </div>
                    ))}
                </div>
              </div>
            )}

            <div
              className="surface-0 border-top-1 surface-border py-4 px-4 flex flex-wrap justify-content-end gap-3 sticky"
              style={{ bottom: "-20px" }}
            >
              <Button
                type="button"
                label={STRING.LOCATION_FORM.CANCEL_BUTTON}
                icon={STRING.LOCATION_FORM.CANCEL_ICON}
                className="p-button-outlined p-button-secondary"
                onClick={() => handleCancel(dirty, resetForm)}
              />
              <Button
                type="submit"
                label={
                  editValues
                    ? STRING.LOCATION_FORM.UPDATE_BUTTON
                    : STRING.LOCATION_FORM.CREATE_BUTTON
                }
                icon={
                  editValues
                    ? STRING.LOCATION_FORM.UPDATE_ICON
                    : STRING.LOCATION_FORM.CREATE_ICON
                }
                className="p-button-primary"
                loading={isSubmitting}
              />
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
